/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable max-len */

import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Switch,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Property } from 'entities/Property/model/types/PropertySchema';
import { FixedSaveBar } from 'shared/ui/FixedSaveBar/FixedSaveBar';

import { DragAndDropBlock } from './DragAndDropBlock';
import { SkeletonProductSourcingSettings } from './SkeletonProductSourcingSettings';
import { SupplierSourceRow } from './SupplierSourceRow';

import { useProductSourcingSettings } from '../lib/useProductSourcingSettings';
import { ProductPriceSortingModes } from '../model/types/ProductSourcingSchema';

type Settings = {
    mode?: string;
    preferredSuppliersIds?: string[];
    propertyId?: string | undefined;
    orderType?: string;
    sortingMode?: string;
};

type ProductSourcingSettingsProps = {
    selectedProperty: Property;
    onApplySettingsToProperty: (arg0: Settings) => Promise<void>;
    orderType: string;
};

export const ProductSourcingSettings = ({
    selectedProperty,
    onApplySettingsToProperty,
    orderType,
}: ProductSourcingSettingsProps) => {
    const { t } = useTranslation();

    const {
        // State
        preferredSuppliers,
        setPreferredSuppliers,
        availableSuppliers,
        setAvailableSuppliers,
        enablePreferredSuppliers,
        setEnablePreferredSuppliers,
        preferredSuppliersOnly,
        setPreferredSuppliersOnly,
        sortingMode,
        setSortingMode,
        sortedAvailableSuppliers,

        // Computed values
        isLoadingData,
        isSaveSettings,
        sortingModeLabel,
        showNumbers,
        saveInProgress,
        friendlyDescription,
        hasPendingChanges,

        // Actions
        saveSettings,
        hasNewSettings,
        cancelChanges,
    } = useProductSourcingSettings(
        selectedProperty,
        orderType,
        onApplySettingsToProperty,
    );

    if (isLoadingData) {
        return <SkeletonProductSourcingSettings />;
    }

    const showPreferredOnlySection =
        enablePreferredSuppliers && preferredSuppliers.length > 0;

    const showPriceSortingSection =
        enablePreferredSuppliers && preferredSuppliers.length > 1;

    return (
        <Box>
            <Typography typography="poppins.h2" marginBottom="20px">
                {t(`Sourcing quantities`)}
            </Typography>
            <Box data-testid="page-description">
                <Typography typography="openSans.body2">
                    {t(
                        'When new items are added to an order, RE-OPS will distribute quantities among your suppliers via the following rules:',
                    )}
                </Typography>
            </Box>
            <FormControl
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    marginTop: '40px',
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        maxWidth: '420px',
                        borderRadius: '12px',
                        padding: '20px 26px',
                        boxSizing: 'border-box',
                        background: 'rgba(233, 232, 232, 0.30)',
                        border: '1px solid #E9E8E8',
                        marginRight: '40px',
                    }}
                >
                    <Typography typography="poppins.h3" marginBottom="22px">
                        {t('Suppliers list')}
                    </Typography>
                    <Box>
                        {enablePreferredSuppliers && (
                            <DragAndDropBlock
                                preferredSuppliers={preferredSuppliers}
                                availableSuppliers={availableSuppliers}
                                isLoadingSuppliers={isLoadingData}
                                setPreferredSuppliers={setPreferredSuppliers}
                                setAvailableSuppliers={setAvailableSuppliers}
                                applySettings={hasNewSettings}
                                isSaveSettings={isSaveSettings}
                                sortingModeLabel={sortingModeLabel}
                                preferredSuppliersOnly={preferredSuppliersOnly}
                                showNumbers={showNumbers}
                                saveInProgress={saveInProgress}
                                autoSortByName={
                                    sortingMode ===
                                    ProductPriceSortingModes.BestPrice
                                }
                            />
                        )}
                        {!enablePreferredSuppliers && (
                            <>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        width: '100%',
                                        justifyContent: enablePreferredSuppliers
                                            ? 'space-between'
                                            : 'flex-end',
                                        flexDirection: 'row',
                                        alignItems: 'flex-end',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: '13px',
                                            lineHeight: '20px',
                                            textAlign: 'right',
                                            marginBottom: '16px',
                                        }}
                                    >
                                        {t('Sort by ')}
                                        <strong>{sortingModeLabel}</strong>
                                    </Typography>
                                </Box>
                                {sortedAvailableSuppliers.map((supplier) => (
                                    <SupplierSourceRow
                                        key={supplier.id}
                                        supplier={supplier}
                                        isDraggable={enablePreferredSuppliers}
                                        isNotUsed={preferredSuppliersOnly}
                                        isDragDisabled={true}
                                    />
                                ))}
                            </>
                        )}
                    </Box>
                </Box>
                <Box sx={{ flex: 1, maxWidth: '520px' }}>
                    <FormControlLabel
                        disabled={saveInProgress}
                        control={
                            <Switch
                                checked={enablePreferredSuppliers}
                                onChange={() => {
                                    const newEnablePreferredSuppliers =
                                        !enablePreferredSuppliers;
                                    let newPreferredSuppliersOnly =
                                        preferredSuppliersOnly;

                                    // Set state
                                    setEnablePreferredSuppliers(
                                        newEnablePreferredSuppliers,
                                    );

                                    // Turn off if we're switching off preferred suppliers
                                    if (enablePreferredSuppliers) {
                                        newPreferredSuppliersOnly = false;
                                        setPreferredSuppliersOnly(
                                            newPreferredSuppliersOnly,
                                        );
                                    }

                                    // Apply settings when user makes changes
                                    hasNewSettings(
                                        {
                                            enablePreferredSuppliers:
                                                newEnablePreferredSuppliers,
                                            preferredSuppliersOnly:
                                                newPreferredSuppliersOnly,
                                            sortingMode,
                                        },
                                        preferredSuppliers,
                                    );
                                }}
                            />
                        }
                        label={t('Enable preferred suppliers')}
                        sx={{
                            marginTop: '9px',
                            '& .MuiFormControlLabel-label': {
                                fontFamily: 'Poppins',
                                fontSize: '17px',
                                fontWeight: 600,
                                lineHeight: '32px',
                            },
                        }}
                    />
                    <Typography typography="openSans.body2" marginTop="14px">
                        {t(
                            'Quantities will be allocated to your preferred suppliers first.',
                        )}
                    </Typography>

                    <Box
                        sx={{
                            overflow: 'hidden',
                            maxHeight: showPreferredOnlySection
                                ? '110px'
                                : '0px',
                            opacity: showPreferredOnlySection ? '1' : '0',
                            pointerEvents: showPreferredOnlySection
                                ? 'auto'
                                : 'none',
                            transition: 'all 0.2s ease-in-out',
                        }}
                    >
                        <FormControlLabel
                            disabled={saveInProgress}
                            control={
                                <Checkbox
                                    disabled={saveInProgress}
                                    checked={preferredSuppliersOnly}
                                    onChange={() => {
                                        const newPreferredSuppliersOnly =
                                            !preferredSuppliersOnly;

                                        // Set state
                                        setPreferredSuppliersOnly(
                                            newPreferredSuppliersOnly,
                                        );

                                        // Apply settings when user makes changes
                                        hasNewSettings(
                                            {
                                                enablePreferredSuppliers,
                                                preferredSuppliersOnly:
                                                    newPreferredSuppliersOnly,
                                                sortingMode,
                                            },
                                            preferredSuppliers,
                                        );
                                    }}
                                />
                            }
                            label={t('Preferred suppliers only')}
                            sx={{
                                marginTop: '26px',
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '15px',
                                    fontWeight: 600,
                                    lineHeight: '20px',
                                },
                            }}
                        />
                        <Typography
                            component="div"
                            typography="openSans.body2"
                            sx={{
                                paddingLeft: '32px',
                            }}
                        >
                            {t(
                                'Quantities can only be distributed to other suppliers manually.',
                            )}
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            overflow: 'hidden',
                            maxHeight: showPriceSortingSection
                                ? '280px'
                                : '0px',
                            opacity: showPriceSortingSection ? '1' : '0',
                            pointerEvents: showPriceSortingSection
                                ? 'auto'
                                : 'none',
                            transition: 'all 0.2s ease-in-out',
                        }}
                    >
                        <Typography
                            typography="poppins.h4"
                            sx={{
                                lineHeight: '32px',
                                marginBottom: '16px',
                                paddingTop: '30px',
                            }}
                        >
                            {t('Sorting options')}
                        </Typography>
                        <Typography typography="openSans.body2">
                            {t(
                                'Select how preferred suppliers will be sorted:',
                            )}
                        </Typography>
                        <RadioGroup
                            name="sorting-options"
                            value={sortingMode}
                            onChange={(e) => {
                                const newSortingMode = e.target.value;

                                // Set state
                                setSortingMode(newSortingMode);

                                // Apply settings when user makes changes
                                hasNewSettings(
                                    {
                                        enablePreferredSuppliers,
                                        preferredSuppliersOnly,
                                        sortingMode: newSortingMode,
                                    },
                                    preferredSuppliers,
                                );
                            }}
                            sx={{
                                marginTop: '24px',
                                '& .MuiFormControlLabel-root': {
                                    borderTop: '1px solid #D9D9D9',
                                    padding: '11px 0',
                                    boxSizing: 'border-box',
                                    marginLeft: 0,
                                    '&:last-of-type': {
                                        borderBottom: '1px solid #D9D9D9',
                                    },
                                },
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '15px',
                                    fontWeight: 600,
                                    lineHeight: '20px',
                                },
                            }}
                        >
                            <FormControlLabel
                                disabled={saveInProgress}
                                value={ProductPriceSortingModes.BestPrice}
                                control={<Radio />}
                                label={t('Best price')}
                            />
                            <FormControlLabel
                                disabled={saveInProgress}
                                value={ProductPriceSortingModes.Custom}
                                control={<Radio />}
                                label={t('Custom list')}
                            />
                        </RadioGroup>
                    </Box>

                    <Box
                        sx={{
                            marginTop: '30px',
                            borderTop: showPriceSortingSection
                                ? 'none'
                                : '1px solid #D9D9D9',
                            paddingTop: showPriceSortingSection
                                ? '0px'
                                : '30px',
                            transition: 'all 0.2s ease-in-out',
                        }}
                    >
                        <Typography
                            typography="poppins.h4"
                            sx={{
                                lineHeight: '32px',
                                marginBottom: '16px',
                            }}
                        >
                            {t('Sourcing outcome')}
                        </Typography>
                        <Typography typography="openSans.body2">
                            {friendlyDescription}
                        </Typography>
                    </Box>
                </Box>
            </FormControl>
            <FixedSaveBar
                show={hasPendingChanges}
                saveLoading={saveInProgress}
                onSave={() => {
                    saveSettings();
                }}
                onCancel={() => {
                    cancelChanges();
                }}
            />
        </Box>
    );
};
